import React, { useMemo } from 'react'
import RenderHTML from './RenderHTML'
import { useKeypadClick } from '../../hooks/useKeypadClick'
import { isIOS } from '../../utils/utils'

interface NumericKeypadProps {
  onNumberClick: (num: number) => void
  onBackspaceClick: () => void
  className?: string
  buttonClassName?: string
  backspaceIcon?: React.ReactNode
}

const KEYPAD_LAYOUT = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
  [null, 0, 'back']
]

export const NumericKeypad: React.FC<NumericKeypadProps> = ({
  onNumberClick,
  onBackspaceClick,
  className = '',
  buttonClassName = '',
  backspaceIcon
}) => {
  const { keypadRef, handleIOSClick, handleAndroidClick } = useKeypadClick({
    onNumberClick,
    onBackspaceClick
  })

  // Memoize screen size check to avoid repeated calculations
  const isSmallScreen = useMemo(() => {
    if (typeof window !== 'undefined' && window?.innerWidth < 700) {
      return true
    }
    return false
  }, [])

  return (
    <div
      ref={keypadRef}
      test-locator='MFE_SELECT_AMOUNT_CTA_ONE_TIME'
      className={`w-full h-full flex flex-col justify-center items-center ${className}`}
      style={
        {
          userSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent'
        } as React.CSSProperties
      }
      {...(isIOS()
        ? {
            onClick: handleIOSClick,
            onTouchStart: handleIOSClick,
            onTouchEnd: (e) => e.preventDefault()
          }
        : {})}
    >
      {KEYPAD_LAYOUT.map((row, rowIdx) => (
        <div
          key={rowIdx}
          className={`flex flex-row justify-center items-center ${isSmallScreen ? 'mb-2' : 'mb-6'} last:mb-0 gap-16 w-full `}
        >
          {row.map((item, colIdx) => {
            if (typeof item === 'number') {
              return (
                <div
                  key={`num-${item}-r${rowIdx}-c${colIdx}`}
                  data-keypad-item={item}
                  className={`text-[22px] font-medium text-[#232323] w-[64px] flex flex-1 h-[48px] !px-0 !py-0 items-center 
                    justify-center bg-transparent border-none outline-none focus:outline-none hover:outline-none ${buttonClassName}`}
                  tabIndex={0}
                  aria-label={`Number ${item}`}
                  {...(!isIOS()
                    ? {
                        onTouchStart: handleAndroidClick(item)
                      }
                    : {})}
                >
                  {item}
                </div>
              )
            } else if (item === 'back') {
              return (
                <div
                  key={`backspace-r${rowIdx}-c${colIdx}`}
                  data-keypad-item='back'
                  className={`w-[64px] flex flex-1 h-[48px] !px-0 !py-0 items-center justify-center 
                    bg-transparent border-none outline-none focus:outline-none hover:outline-none ${buttonClassName}`}
                  tabIndex={0}
                  aria-label='Backspace'
                  {...(!isIOS()
                    ? {
                        onTouchStart: handleAndroidClick('back')
                      }
                    : {})}
                >
                  {backspaceIcon || <RenderHTML innerHTMLText={DeleteIcon} />}
                </div>
              )
            } else {
              // Empty cell for layout
              return (
                <div
                  key={`empty-r${rowIdx}-c${colIdx}`}
                  className='w-[64px] flex flex-1 h-[48px]'
                  data-keypad-empty='true'
                />
              )
            }
          })}
        </div>
      ))}
    </div>
  )
}

const DeleteIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="27" height="26" viewBox="0 0 27 26" fill="none">
  <g clip-path="url(#clip0_37791_6549)">
    <path d="M13.167 9.97183L19.2237 16.0287" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M19.2228 9.97168L13.166 16.0285" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M22.6674 5.21289H9.44144C8.96068 5.21289 8.50116 5.40904 8.17106 5.7552L2.39498 11.812C1.75897 12.4789 1.75896 13.5215 2.39498 14.1884L8.17106 20.2452C8.50116 20.5913 8.96068 20.7876 9.44144 20.7876H22.6674C23.6324 20.7876 24.4147 20.0128 24.4147 19.057V6.94341C24.4147 5.98768 23.6324 5.21289 22.6674 5.21289Z" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_37791_6549">
      <rect width="26" height="26" fill="white" transform="translate(0.166016)"/>
    </clipPath>
  </defs>
</svg>`

export default NumericKeypad
